/**
 * AnnotationSetList Component
 *
 * Lists annotation sets for a dataset with management actions.
 */

import { useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from '@heroui/react';
import { Lock, Plus, Trash2 } from 'lucide-react';

import {
  useAnnotationSets,
  useCreateAnnotationSet,
  useDeleteAnnotationSet,
  usePublishAnnotationSet,
} from '@/lib/api/hooks/annotations';
import { DataTable, type Column } from '@/components/data/DataTable';
import { formatRelativeTime } from '@/lib/design-system';
import type { AnnotationSetResponse } from '@/api';

interface AnnotationSetListProps {
  datasetId: string;
  onSelectSet: (setId: string) => void;
}

export function AnnotationSetList({ datasetId, onSelectSet }: AnnotationSetListProps) {
  const { data: annotationSets, isLoading } = useAnnotationSets(datasetId);
  const createSet = useCreateAnnotationSet(datasetId);
  const deleteSet = useDeleteAnnotationSet(datasetId);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [setToDelete, setSetToDelete] = useState<AnnotationSetResponse | null>(null);
  const [publishingSetId, setPublishingSetId] = useState<string | null>(null);

  const handleCreateSet = async () => {
    try {
      const newSet = await createSet.mutateAsync();
      if (newSet?.id) {
        onSelectSet(newSet.id);
      }
    } catch {
      // Error handled by mutation
    }
  };

  const handleDeleteClick = (set: AnnotationSetResponse) => {
    setSetToDelete(set);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (setToDelete) {
      try {
        await deleteSet.mutateAsync(setToDelete.id);
        setDeleteModalOpen(false);
        setSetToDelete(null);
      } catch {
        // Error handled by mutation
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'success';
      case 'draft':
        return 'warning';
      default:
        return 'default';
    }
  };

  const columns: Column<AnnotationSetResponse>[] = [
    {
      key: 'version',
      label: 'Version',
      render: (row) => <span className="font-medium">v{row.version}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (
        <Chip size="sm" variant="flat" color={getStatusColor(row.status)}>
          {row.status}
        </Chip>
      ),
    },
    {
      key: 'total_annotations',
      label: 'Annotations',
      align: 'end',
    },
    {
      key: 'created_at',
      label: 'Created',
      hideOnMobile: true,
      render: (row) => formatRelativeTime(new Date(row.created_at)),
    },
    {
      key: 'actions',
      label: '',
      align: 'end',
      render: (row) => (
        <div className="flex items-center justify-end gap-1">
          <PublishButton
            datasetId={datasetId}
            set={row}
            publishingSetId={publishingSetId}
            setPublishingSetId={setPublishingSetId}
          />
          <Button
            size="sm"
            variant="light"
            color="danger"
            isIconOnly
            isDisabled={row.status === 'published' || deleteSet.isPending}
            onPress={() => handleDeleteClick(row)}
            aria-label={`Delete version ${row.version}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const sets = annotationSets?.items ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardBody>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Annotation Versions</h3>
            <Button
              size="sm"
              color="primary"
              startContent={<Plus className="h-4 w-4" />}
              onPress={handleCreateSet}
              isLoading={createSet.isPending}
            >
              New Version
            </Button>
          </div>

          <DataTable
            columns={columns}
            data={sets}
            emptyMessage="No annotation sets"
            emptyDescription="Create a new version to start annotating."
            onRowClick={(row) => onSelectSet(row.id)}
          />
        </CardBody>
      </Card>

      <Modal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <ModalContent>
          <ModalHeader>Delete Annotation Set</ModalHeader>
          <ModalBody>
            <p>
              Are you sure you want to delete{' '}
              <span className="font-medium">version {setToDelete?.version}</span>?
            </p>
            <p className="mt-2 text-sm text-default-500">
              This action cannot be undone. All annotations in this set will be deleted.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="danger"
              onPress={handleDeleteConfirm}
              isLoading={deleteSet.isPending}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

function PublishButton({
  datasetId,
  set,
  publishingSetId,
  setPublishingSetId,
}: {
  datasetId: string;
  set: AnnotationSetResponse;
  publishingSetId: string | null;
  setPublishingSetId: (id: string | null) => void;
}) {
  const publishSet = usePublishAnnotationSet(datasetId, set.id);

  const handlePublish = async () => {
    setPublishingSetId(set.id);
    try {
      await publishSet.mutateAsync();
    } catch {
      // Error handled by mutation
    } finally {
      setPublishingSetId(null);
    }
  };

  if (set.status === 'published') {
    return null;
  }

  return (
    <Button
      size="sm"
      variant="flat"
      color="success"
      startContent={<Lock className="h-3 w-3" />}
      isLoading={publishingSetId === set.id}
      isDisabled={publishingSetId !== null}
      onPress={handlePublish}
    >
      Publish
    </Button>
  );
}
